/**
 * QUIPWA — skins: the optional look of the controls
 *
 * A skin is CSS, not a theme. Colours still come from the active theme (and
 * its light and night versions); a skin only adds light, relief, texture and
 * shape to the roles in styles/primitives.css. It is a device preference, like
 * light mode, kept in the theme store.
 *
 *   plana     the flat look, built in: costs nothing to load
 *   fichero   the library card catalogue and the Leitner box (docs/plan/piel-fichero.md),
 *             in three thicknesses: sutil, clásico (the default), tosco
 *
 * Each skin's stylesheet is its own chunk, downloaded only by whoever turns it
 * on (scripts/check-bundle.mjs keeps it off the first screen) and precached
 * for offline use like any chunk. Its rules all hang from
 * `:root[data-skin='…']`, so having it loaded is harmless until the attribute
 * says so.
 *
 * ```ts
 * await loadSkin('fichero')          // the stylesheet is in and applies
 * applySkin('fichero', 'clasico')    // apply-theme.ts: the attributes on <html>
 * ```
 */
export const SKINS = ['plana', 'fichero'] as const
export type Skin = (typeof SKINS)[number]

export const THICKNESSES = ['sutil', 'clasico', 'tosco'] as const
export type Thickness = (typeof THICKNESSES)[number]

const STYLESHEETS: Record<Exclude<Skin, 'plana'>, () => Promise<unknown>> = {
  fichero: () => import('../../styles/skins/fichero/fichero.css'),
}

const loading = new Map<Skin, Promise<void>>()

/**
 * The skin's stylesheet, loaded once. Resolves when it applies (in a build,
 * Vite resolves the import only after the <link> has loaded). Never rejects:
 * a skin that fails to load leaves the flat look, which is complete.
 */
export function loadSkin(skin: Skin): Promise<void> {
  if (skin === 'plana') return Promise.resolve()
  let pending = loading.get(skin)
  if (!pending) {
    pending = STYLESHEETS[skin]().then(
      () => undefined,
      (err: unknown) => {
        loading.delete(skin) // a later attempt may succeed (back online)
        console.warn(`[Skin] Could not load «${skin}»`, err)
      },
    )
    loading.set(skin, pending)
  }
  return pending
}

/** Before the first render: the skin's stylesheet, but never more than `ms`. */
export function waitForSkin(skin: Skin, ms = 1500): Promise<void> {
  if (skin === 'plana') return Promise.resolve()
  return Promise.race([loadSkin(skin), new Promise<void>((done) => setTimeout(done, ms))])
}

export function isSkin(value: unknown): value is Skin {
  return (SKINS as readonly unknown[]).includes(value)
}

export function isThickness(value: unknown): value is Thickness {
  return (THICKNESSES as readonly unknown[]).includes(value)
}
